import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, ArrowRight, Loader2 } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Group locations by county
const groupByCounty = (locations) => {
  return locations.reduce((acc, loc) => {
    if (!acc[loc.county]) acc[loc.county] = [];
    acc[loc.county].push(loc);
    return acc;
  }, {});
};

export default function Locations() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${API}/locations`);
        setLocations(response.data);
      } catch (error) {
        console.error("Error fetching locations:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchLocations();
  }, []);

  const groupedLocations = groupByCounty(locations);

  return (
    <div data-testid="locations-page" className="pt-20">
      <SEOHead
        title="Service Areas - DFW Metroplex Junk Removal"
        description="Overhaul Ops provides junk removal services across the DFW metroplex including Denton, Tarrant, Parker, and Wise counties."
        keywords="junk removal service areas, DFW junk removal, Denton County, Tarrant County, Parker County, Wise County"
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Locations", url: "/locations" }]}
      />
      {/* Hero */}
      <section className="py-20 md:py-32 bg-[#1a1a1a] noise-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#b51a00] font-bold uppercase tracking-widest text-sm">
              Service Areas
            </span>
            <h1 
              className="text-5xl md:text-7xl font-black text-white uppercase leading-none mt-2 mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.04em' }}
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Serving <span className="text-[#b51a00]">DFW</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Professional junk removal services across Denton, Tarrant, Parker, and Wise counties.
            </p>
          </div>
        </div>
      </section>

      {/* Counties */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#b51a00]" />
            </div>
          ) : Object.keys(groupedLocations).length > 0 ? (
            <div className="space-y-16">
              {Object.entries(groupedLocations).map(([county, cities]) => (
                <div key={county}>
                  <h2 
                    className="text-3xl font-bold uppercase tracking-tight mb-8 pb-4 border-b"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    <MapPin className="inline w-6 h-6 mr-2 text-[#b51a00]" />
                    {county} County
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {cities.map((city) => (
                      <Link key={city.id} to={`/locations/${city.slug}`}>
                        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow group">
                          {city.image_url && (
                            <div className="aspect-video overflow-hidden">
                              <img 
                                src={city.image_url} 
                                alt={city.city}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              />
                            </div>
                          )}
                          <CardContent className="p-6">
                            <h3 
                              className="text-xl font-bold group-hover:text-[#b51a00] transition-colors"
                              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                            >
                              {city.city}, TX
                            </h3>
                            <p className="text-gray-500 text-sm mt-1 line-clamp-2">
                              {city.meta_description}
                            </p>
                            <span className="inline-flex items-center text-[#b51a00] text-sm mt-3 font-medium">
                              Learn More <ArrowRight className="ml-1 w-4 h-4" />
                            </span>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <h2 
                className="text-3xl font-bold mb-6"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Our Service Counties
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
                {["Denton County", "Tarrant County", "Parker County", "Wise County"].map((county) => (
                  <div key={county} className="bg-[#f4f4f5] p-6 rounded-sm text-center">
                    <MapPin className="w-8 h-8 mx-auto mb-2 text-[#b51a00]" />
                    <span className="font-bold">{county}</span>
                  </div>
                ))}
              </div>
              <p className="text-gray-500 mt-8">
                Contact us for service in your specific city!
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#b51a00]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-4xl md:text-5xl font-bold uppercase tracking-wide text-white mb-6"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Service Your Area?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Get a free quote for junk removal in your city today.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-[#b51a00] hover:bg-gray-100 font-bold uppercase tracking-wider rounded-sm px-8 py-6 text-lg">
              Get Your Free Quote
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
